// Primary Sales Return (distributor -> company) — distributor files a KG
// return, line by line: SKU (ALL skus, not just ones currently mapped/
// ordered — a return can be old/discontinued stock), qty in kg, packing
// month, and a Plain/Scheme split (scheme lines need a scheme name instead
// of a reason code). ASM approves first, then ZSM — ZSM is final.
import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Loader2, PackageMinus, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { authenticatedDownload } from "@/components/bulk/useBulkJob";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const STATUS_STYLE = {
  submitted:     "bg-amber-50 text-amber-700 border-amber-200",
  asm_approved:  "bg-sky-50 text-sky-700 border-sky-200",
  asm_rejected:  "bg-rose-50 text-rose-700 border-rose-200",
  zsm_approved:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  zsm_rejected:  "bg-rose-50 text-rose-700 border-rose-200",
};
function statusLabel(s) {
  return (s || "").replace(/_/g, " ");
}

function emptyLine() {
  return { key: Math.random().toString(36).slice(2), sku_id: "", qty_kg: "", packingMonthNum: "", packingYear: "", reason_code: "", type: "plain", scheme_name: "" };
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
// Packing can be old/discontinued stock — go back further than a normal
// "recent months" picker would (5 years covers it with room to spare).
const CURRENT_YEAR = new Date(Date.now() + 5.5 * 3600 * 1000).getFullYear();
const PACKING_YEARS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);

export default function PrimarySalesReturn() {
  const { hasWorkflowPerm } = useAuth();
  const allowed = hasWorkflowPerm("PRIMARY_SALE_RETURN");
  const [skus, setSkus] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [myDistributors, setMyDistributors] = useState([]);
  const [distributorId, setDistributorId] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewReturn, setViewReturn] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const downloadPdf = async (r) => {
    setPdfBusy(true);
    try {
      await authenticatedDownload(`/primary-sale-returns/${r.id}/pdf`, `${r.return_no}.pdf`);
    } catch (e) { toast.error(`PDF download failed: ${e?.message || e}`); }
    finally { setPdfBusy(false); }
  };

  useEffect(() => {
    if (!allowed) return;
    api.get("/primary-sale-returns/skus", { params: { page_size: 2000 } })
      .then((r) => setSkus(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)));
    api.get("/primary-sale-returns/reasons")
      .then((r) => setReasons(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)));
    // Most distributor logins map to exactly one distributor, but a shared
    // login (e.g. a combined "X # Y" code) can map to several — only show
    // the picker when there's actually a choice to make.
    api.get("/primary-sale-returns/my-distributors")
      .then((r) => {
        const items = r.data.items || [];
        setMyDistributors(items);
        if (items.length === 1) setDistributorId(items[0].id);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [allowed]);

  const loadReturns = () => {
    setLoading(true);
    api.get("/primary-sale-returns", { params: { page_size: 50 } })
      .then((r) => setReturns(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (allowed) loadReturns(); }, [allowed]);

  const skuNameById = useMemo(() => new Map(skus.map((s) => [s.id, s.name])), [skus]);

  const summary = useMemo(() => {
    const byGroup = new Map();
    const bySku = [];
    let total = 0;
    for (const l of lines) {
      const kg = parseFloat(l.qty_kg) || 0;
      if (!kg || !l.sku_id) continue;
      const key = l.type === "scheme" ? (l.scheme_name.trim() || "Scheme") : "Plain";
      byGroup.set(key, (byGroup.get(key) || 0) + kg);
      total += kg;
      bySku.push({
        key: l.key,
        name: skuNameById.get(l.sku_id) || l.sku_id,
        kg,
        group: key,
      });
    }
    return {
      total: Math.round(total * 1000) / 1000,
      byGroup: [...byGroup.entries()],
      bySku,
    };
  }, [lines, skuNameById]);

  const setLine = (key, patch) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (key) => setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.key !== key) : ls));

  // Name first, code last — the trigger button truncates with an ellipsis,
  // and a code-first label ("FG00093 — TULSI SPECIAL FANNINGS...") cuts off
  // the part that actually identifies the SKU, leaving just a code fragment
  // visible. Name-first means truncation trims the (already-redundant) code
  // instead of the name.
  const skuOptions = skus.map((s) => ({ id: s.id, label: `${s.name} — ${s.sku_code}` }));

  const canSubmit = !!distributorId && lines.every((l) =>
    l.sku_id && parseFloat(l.qty_kg) > 0 && l.packingMonthNum && l.packingYear &&
    l.reason_code &&
    (l.type === "plain" || (l.type === "scheme" && l.scheme_name.trim())));

  const submit = async () => {
    if (!canSubmit) {
      toast.error(!distributorId
        ? "Select which distributor this return is for."
        : "Fill every line completely — SKU, kg, packing month, reason, and scheme name if Scheme.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/primary-sale-returns", {
        distributor_id: distributorId,
        lines: lines.map((l) => ({
          sku_id: l.sku_id, qty_kg: parseFloat(l.qty_kg),
          packing_month: `${l.packingYear}-${l.packingMonthNum}`,
          reason_code: l.reason_code,
          type: l.type, scheme_name: l.type === "scheme" ? l.scheme_name.trim() : null,
        })),
        remarks: remarks.trim() || null,
      });
      toast.success("Return submitted for ASM approval");
      setLines([emptyLine()]);
      setRemarks("");
      loadReturns();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  if (!allowed) {
    return (
      <div className="p-6 text-center text-sm text-neutral-500" data-testid="primary-sr-not-allowed">
        You don't have permission to file Primary Sales Returns.
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-24" data-testid="primary-sr-page">
      <div>
        <h1 className="text-lg font-extrabold text-neutral-900 flex items-center gap-2">
          <PackageMinus className="h-5 w-5 text-[#6D2C91]" /> Primary Sales Return
        </h1>
        <p className="text-xs text-neutral-500">
          Return stock to the company — any SKU, including old/discontinued packing. Goes to ASM then ZSM for approval.
        </p>
      </div>

      {myDistributors.length > 1 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">Distributor</div>
          <Select value={distributorId} onValueChange={setDistributorId}>
            <SelectTrigger data-testid="psr-distributor-picker">
              <SelectValue placeholder="Choose distributor…" />
            </SelectTrigger>
            <SelectContent>
              {myDistributors.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.code} · {d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-3">
        {lines.map((l, i) => (
          <Card key={l.key} className="border-neutral-200 rounded-2xl" data-testid={`psr-line-${i}`}>
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-neutral-500">Line {i + 1}</span>
                {lines.length > 1 && (
                  <button type="button" onClick={() => removeLine(l.key)} data-testid={`psr-line-remove-${i}`}>
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </button>
                )}
              </div>
              <SearchableSelect
                value={l.sku_id} onChange={(id) => setLine(l.key, { sku_id: id })}
                options={skuOptions} placeholder="Select SKU (all SKUs, any status)"
                testid={`psr-line-sku-${i}`}
              />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">Kg Returned</div>
                <Input type="number" min="0" step="0.001" placeholder="Kg returned"
                       value={l.qty_kg} onChange={(e) => setLine(l.key, { qty_kg: e.target.value })}
                       data-testid={`psr-line-kg-${i}`} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1">Packing Month</div>
                <div className="grid grid-cols-2 gap-2">
                  <Select value={l.packingMonthNum} onValueChange={(v) => setLine(l.key, { packingMonthNum: v })}>
                    <SelectTrigger data-testid={`psr-line-packing-month-num-${i}`}>
                      <SelectValue placeholder="Month" />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTH_NAMES.map((name, idx) => (
                        <SelectItem key={name} value={String(idx + 1).padStart(2, "0")}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={l.packingYear} onValueChange={(v) => setLine(l.key, { packingYear: v })}>
                    <SelectTrigger data-testid={`psr-line-packing-year-${i}`}>
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {PACKING_YEARS.map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Select value={l.reason_code} onValueChange={(v) => setLine(l.key, { reason_code: v })}>
                <SelectTrigger data-testid={`psr-line-reason-${i}`}>
                  <SelectValue placeholder="Reason for return" />
                </SelectTrigger>
                <SelectContent>
                  {reasons.map((r) => (
                    <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <RadioGroup value={l.type} onValueChange={(v) => setLine(l.key, { type: v })}
                          className="flex items-center gap-4" data-testid={`psr-line-type-${i}`}>
                <label className="flex items-center gap-1.5 text-sm">
                  <RadioGroupItem value="plain" /> Plain
                </label>
                <label className="flex items-center gap-1.5 text-sm">
                  <RadioGroupItem value="scheme" /> Scheme
                </label>
              </RadioGroup>
              {l.type === "scheme" && (
                <Input placeholder="Scheme name (required)"
                       value={l.scheme_name} onChange={(e) => setLine(l.key, { scheme_name: e.target.value })}
                       data-testid={`psr-line-scheme-name-${i}`} />
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Button variant="outline" onClick={addLine} className="w-full" data-testid="psr-add-line">
        <Plus className="h-4 w-4 mr-1.5" /> Add another SKU
      </Button>

      {summary.total > 0 && (
        <Card className="border-neutral-200 rounded-2xl bg-neutral-50/60" data-testid="psr-summary">
          <CardContent className="p-3 space-y-2">
            <div className="text-[11px] uppercase tracking-widest text-neutral-400">Summary</div>

            <div className="space-y-1">
              {summary.bySku.map((row) => (
                <div key={row.key} className="flex items-center justify-between gap-2 text-xs text-neutral-700"
                     data-testid={`psr-summary-sku-${row.key}`}>
                  <span className="truncate">{row.name}</span>
                  <span className="shrink-0 text-neutral-500">{row.group} · {Math.round(row.kg * 1000) / 1000} kg</span>
                </div>
              ))}
            </div>

            <div className="border-t border-dashed border-neutral-300 pt-1.5 space-y-1">
              {summary.byGroup.map(([label, kg]) => (
                <div key={label} className="flex items-center justify-between text-xs text-neutral-600">
                  <span>{label}</span><span>{Math.round(kg * 1000) / 1000} kg</span>
                </div>
              ))}
              <div className="flex items-center justify-between text-sm font-semibold text-neutral-900">
                <span>Total</span><span data-testid="psr-summary-total">{summary.total} kg</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Textarea placeholder="Any additional remarks for the whole return (optional)"
                value={remarks} onChange={(e) => setRemarks(e.target.value)}
                data-testid="psr-remarks" />

      <Button className="tulsi-gradient text-white w-full h-12 rounded-xl" onClick={submit}
              disabled={submitting || !canSubmit} data-testid="psr-submit">
        {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
        Submit for ASM Approval
      </Button>

      <div className="pt-2">
        <div className="text-[11px] uppercase tracking-widest text-neutral-400 mb-2">Past Returns</div>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" />
        ) : returns.length === 0 ? (
          <div className="text-sm text-neutral-500 text-center py-6">No returns filed yet.</div>
        ) : (
          <div className="space-y-2">
            {returns.map((r) => (
              <Card key={r.id} className="border-neutral-200 rounded-2xl cursor-pointer active:bg-neutral-50"
                    onClick={() => setViewReturn(r)} data-testid={`psr-history-${r.id}`}>
                <CardContent className="p-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-neutral-900">{r.return_no}</div>
                    <div className="text-[11px] text-neutral-500">
                      {r.distributor_name ? `${r.distributor_name} · ` : ""}{r.total_kg} kg · {new Date(r.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <Badge className={"border text-[10px] capitalize " + (STATUS_STYLE[r.status] || "")}
                         data-testid={`psr-history-status-${r.id}`}>
                    {statusLabel(r.status)}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!viewReturn} onOpenChange={(v) => !v && setViewReturn(null)}>
        <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto" data-testid="psr-view-dialog">
          {viewReturn && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between gap-2 pr-6">
                  <span>{viewReturn.return_no}</span>
                  <Badge className={"border text-[10px] capitalize " + (STATUS_STYLE[viewReturn.status] || "")}>
                    {statusLabel(viewReturn.status)}
                  </Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="text-[11px] text-neutral-500">
                {viewReturn.distributor_name && (
                  <>{viewReturn.distributor_code} — {viewReturn.distributor_name} · </>
                )}
                Filed {new Date(viewReturn.created_at).toLocaleString()}
              </div>

              <Button variant="outline" size="sm" onClick={() => downloadPdf(viewReturn)}
                      disabled={pdfBusy} className="w-full" data-testid="psr-view-download-pdf">
                {pdfBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                Download PDF
              </Button>

              <div className="space-y-2">
                {(viewReturn.lines || []).map((ln) => (
                  <div key={ln.id} className="rounded-xl border border-neutral-200 p-2.5 text-xs space-y-0.5"
                       data-testid={`psr-view-line-${ln.id}`}>
                    <div className="font-semibold text-neutral-900">{ln.sku_name || ln.sku_code}</div>
                    <div className="text-neutral-500">{ln.sku_code}</div>
                    <div className="flex items-center justify-between text-neutral-600 pt-0.5">
                      <span>{ln.qty_kg} kg · {ln.packing_month}</span>
                      <span>{ln.reason_label || ln.reason_code}</span>
                    </div>
                    <div className="text-neutral-600">
                      {ln.type === "scheme" ? `Scheme: ${ln.scheme_name}` : "Plain"}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between text-sm font-semibold text-neutral-900 pt-1 border-t border-dashed border-neutral-200">
                <span>Total</span><span>{viewReturn.total_kg} kg</span>
              </div>

              {viewReturn.remarks && (
                <div className="text-xs text-neutral-600 italic">{viewReturn.remarks}</div>
              )}

              <div className="space-y-1 pt-1 border-t border-dashed border-neutral-200 text-xs text-neutral-600">
                {(viewReturn.approval_chain || []).length === 0 ? (
                  <div className="text-neutral-400">No approval activity yet.</div>
                ) : (
                  viewReturn.approval_chain.map((a, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="uppercase">{a.step} · {a.by_name || a.by}</span>
                      <span className={a.action === "reject" ? "text-rose-600" : "text-emerald-700"}>
                        {a.action === "reject" ? "Rejected" : "Approved"}
                        {a.notes ? ` — ${a.notes}` : ""}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
